import React, { ReactNode } from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { ITheme } from '../../../themer';
import themed from '../../../themer/themed';
import { SelectCurrent } from './SelectCurrent';
import { SelectPlaceholder } from './SelectPlaceholder';
import { SelectToggleIcon } from './SelectToggleIcon';
import {
	DropdownItem,
	IDropdownItem,
	IGetItemDisplayValue,
} from '../../DropdownList';
import { appendErrorBoxShadow, CloseIcon } from '../..';

interface IStyledSelectMainProps {
	disabled?: boolean;
	invalid?: boolean;
	className?: string;
}

export const StyledSelectMain = styled.div<IStyledSelectMainProps, ITheme>`
	display: flex;
	position: relative;
	padding: ${themed(({ select }) => select.main.padding)};
	border: ${themed(({ select }) => select.main.border)};
	background: ${themed(({ select }, { disabled }) =>
		disabled ? select.disabled.background : select.main.background,
	)};
	border-radius: ${themed(({ select }) => select.main.borderRadius)};
	color: ${themed(({ select }, { disabled }) =>
		disabled ? select.disabled.color : select.main.color,
	)};
	box-shadow: ${themed(({ select, input }, { disabled, invalid }) => {
		const style = disabled ? select.disabled.boxShadow : select.main.boxShadow;

		return appendErrorBoxShadow(style, input, invalid);
	})};
	font-size: ${themed(({ select }) => select.main.fontSize)};
	cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

	&&:focus,
	&&.is-open {
		background: ${themed(({ select }) => select.focus.background)};
		color: ${themed(({ select }) => select.focus.color)};
		box-shadow: ${themed(({ select }) => select.focus.boxShadow)};
		outline: none;
	}

	${SelectCurrent} {
		flex-grow: 1;
	}
`;

export const SelectInput = styled.input<{}, ITheme>`
	position: absolute;
	left: -9999px;
	width: 1px;
	height: 1px;
`;

const SelectIcon = styled.div<{}, ITheme>`
	path {
		fill: ${themed(({ input }) => input.icon.background)};
	}
`;

interface ISelectMainProps {
	getInputProps: PropGetters<IDropdownItem>['getInputProps'];
	getToggleButtonProps: PropGetters<IDropdownItem>['getToggleButtonProps'];
	getItemDisplayValue: IGetItemDisplayValue;
	selectedItem: IDropdownItem | null;
	placeholder?: string;
	ariaLabel?: string;
	ariaLabelledBy?: string;
	disabled?: boolean;
	invalid?: boolean;
	allowsRemovingSelection?: boolean;
	onClickRemoveSelection?: () => void;
	isOpen: boolean;
	icon?: ReactNode;
}

export const SelectMain = ({
	getInputProps,
	getToggleButtonProps,
	selectedItem,
	placeholder,
	ariaLabel,
	ariaLabelledBy,
	getItemDisplayValue,
	isOpen,
	disabled,
	invalid,
	allowsRemovingSelection,
	onClickRemoveSelection,
	icon,
}: ISelectMainProps) => {
	const hint =
		selectedItem &&
		typeof selectedItem === 'object' &&
		selectedItem.type === 'item'
			? selectedItem.hint
			: undefined;

	const mainProps = disabled
		? {}
		: getToggleButtonProps({
				role: 'combobox',
				'aria-haspopup': 'listbox',
				'aria-expanded': isOpen,
				'aria-controls': getInputProps()['aria-controls'],
				'aria-activedescendant': getInputProps()['aria-activedescendant'],
		  });

	const shouldShowOpenCloseIcon = allowsRemovingSelection
		? !disabled && selectedItem === null
		: !disabled;

	const shouldShowRemoveSelectionIcon = !shouldShowOpenCloseIcon;

	return (
		<>
			<SelectInput
				{...(getInputProps({
					disabled,
				}) as React.InputHTMLAttributes<HTMLInputElement>)}
				id={undefined}
				aria-invalid={invalid}
				aria-labelledby={ariaLabelledBy}
				{...({ inert: '' } as any)}
			/>
			<StyledSelectMain
				{...mainProps}
				aria-label={
					ariaLabel ??
					(ariaLabelledBy ? undefined : getToggleButtonProps()['aria-label'])
				}
				aria-labelledby={ariaLabel ? undefined : ariaLabelledBy}
				disabled={disabled}
				invalid={invalid}
				tabIndex={0}
				className={isOpen ? 'is-open' : ''}
			>
				<SelectCurrent>
					{selectedItem ? (
						<DropdownItem
							displayValue={getItemDisplayValue(selectedItem)}
							hint={hint}
						/>
					) : (
						<SelectPlaceholder>{placeholder}</SelectPlaceholder>
					)}
				</SelectCurrent>
				{shouldShowOpenCloseIcon &&
					(icon ? (
						<SelectIcon>{icon}</SelectIcon>
					) : (
						<SelectToggleIcon isOpen={isOpen} />
					))}

				{shouldShowRemoveSelectionIcon && (
					<CloseIcon
						aria-label="Remove selection"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();

							if (onClickRemoveSelection) {
								onClickRemoveSelection();
							}
						}}
					/>
				)}
			</StyledSelectMain>
		</>
	);
};
